import type { BlockField } from '@/blocks/domain/types';
import type { CustomContent } from '@/blocks/domain/types/BlockContent/CustomContent';
import type { HeaderSection } from '@/blocks/domain/types/BlockContent/HeaderContent';
import { createBlockField } from './createBlockField';

/**
 * The Custom block on the section pipeline (LLAMS-610, Figma `39:90`). A Custom block is a stack of
 * sections of Heading / Text / Label + value elements, the same model a Letterhead uses. Blocks
 * saved before that model carry `title` + `bodyHtml` only; they keep rendering as prose everywhere
 * and are promoted to the sectioned shape inside the block editor alone.
 */

/** Fixed ids: the editor builds its draft AND its clean baseline from the same content, so a random id would read as an unsaved edit. */
export const CUSTOM_SECTION_ID = 'sec-custom-1';
const HEADING_FIELD_ID = 'fld-custom-heading';
const TEXT_FIELD_ID = 'fld-custom-text';

/**
 * True when the content is on the section model. Takes any record with an optional `sections` so the
 * typed content and a normalised imported snapshot can both ask. Absent or empty means legacy prose.
 */
export function customUsesSections(content: { sections?: unknown }): boolean {
  return Array.isArray(content.sections) && content.sections.length > 0;
}

/** The one section a fresh (or just promoted) Custom block starts with. */
export function defaultCustomSection(): HeaderSection {
  return { id: CUSTOM_SECTION_ID, layout: '1' };
}

/**
 * `rich_text` is the "Text" element: its value is HTML, which is what the legacy body holds. Like a Text
 * added from the Add panel it carries no name of its own; every surface reads "Text" from `fieldStyleLabel`.
 */
function seededField(style: 'heading' | 'rich_text', value: string): BlockField {
  const isHeading = style === 'heading';
  return {
    ...createBlockField(style, isHeading ? 0 : 1),
    id: isHeading ? HEADING_FIELD_ID : TEXT_FIELD_ID,
    value,
    sectionId: CUSTOM_SECTION_ID,
    column: 0
  };
}

/** A new Custom block: one 1-column section holding an empty Heading and an empty Text. */
export function defaultCustomContent(): CustomContent {
  return {
    kind: 'custom',
    title: '',
    bodyHtml: '',
    sections: [defaultCustomSection()],
    fields: [seededField('heading', ''), seededField('rich_text', '')]
  };
}

/**
 * Legacy `{ title, bodyHtml }` to the sectioned shape, keeping both texts: the title becomes a Heading
 * and the body a Text element in one 1-column section. A blank side gets no element. `title` and
 * `bodyHtml` are cleared so the content is read one way only.
 */
export function promoteLegacyCustom(content: CustomContent): CustomContent {
  const fields: BlockField[] = [];
  if (content.title.trim() !== '') fields.push(seededField('heading', content.title));
  if (content.bodyHtml.trim() !== '') fields.push(seededField('rich_text', content.bodyHtml));
  return {
    ...content,
    title: '',
    bodyHtml: '',
    sections: [defaultCustomSection()],
    fields: fields.map((field, index) => ({ ...field, sortOrder: index }))
  };
}
